'use client';

import { useState, useEffect, useMemo } from 'react';
import { Play, ChevronRight, ChevronLeft, Star, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '@/lib/hooks/use-theme';
import { useFrontSession } from '@/components/front-session-provider';
import { cn } from '@/lib/utils';

/**
 * Cinematic Hero Component
 * Rockstar-inspired full-screen hero section for premium e-commerce experience
 * Features: Dark aesthetics, bold typography, dramatic animations
 */

interface CinematicHeroProps {
  className?: string;
  videoBackground?: boolean;
  parallaxEffect?: boolean;
}

export default function CinematicHero({
  className = '',
  videoBackground = true,
  parallaxEffect = true,
}: CinematicHeroProps) {
  const [mounted, setMounted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { isDark } = useTheme();
  const { authenticated } = useFrontSession();

  const lightLogo = "https://media.akilesecosystem.com/logos/logo-akiles-studio-hero-512.png";
  const darkLogo = "https://media.akilesecosystem.com/logos/logo-akiles-studio-hero-512-tw.png";

  // Memoize slides to ensure they react to authentication changes correctly
  const slides = useMemo(() => [
    {
      title: "Art & Design Studio",
      useBrandLogo: true,
      subtitle: (
        <>
          unique artworks, digital art, stickers, prints and more.
        </>
      ),
      bgImage: 'https://media.akilesecosystem.com/items/digital/digitization/atardecer-reliquias-main.png',
      btn1Text: authenticated ? "Store" : "Explore",
      btn1Href: "/store",
      btn2Text: authenticated ? "Dashboard" : "Connect",
      btn2Href: authenticated ? "/account" : "/auth/login",
    },
    {
      title: (
        <>
          Learn About Our<br />
          Stickers Consignment Network
        </>
      ),
      useBrandLogo: false,
      hideStar: true,
      subtitle: (
        <>
          Join our growing ecosystem of local shops and retailers.
          <br />
          <span className="text-gray-400 text-lg md:text-xl">
            Bringing unique art products to the community together.
          </span>
        </>
      ),
      bgImage: 'https://media.akilesecosystem.com/items/print/giclee-print/el-spot-mural.png',
      btn1Text: authenticated ? "Network" : "Connect",
      btn1Href: "/contact",
      btn2Text: authenticated ? "Dashboard" : "Connect",
      btn2Href: authenticated ? "/account" : "/auth/login",
    },
    {
      title: "Under Development Sections",
      useBrandLogo: false,
      subtitle: (
        <>
          Upcoming sections: Classes, Maker-Space, and Web3 Gallery.
          <br />
          <span className="text-gray-400 text-lg md:text-xl">
            Play Seven Elements game experience soon.
          </span>
        </>
      ),
      bgImage: 'https://media.akilesecosystem.com/items/digital/animation/exiliado-animation-main.png',
      btn1Text: "View Portfolio",
      btn1Href: "/portfolio",
      btn2Text: authenticated ? "Dashboard" : "Connect",
      btn2Href: authenticated ? "/account" : "/auth/login",
    }
  ], [authenticated]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (parallaxEffect) {
      const handleScroll = () => {
        setScrollY(window.scrollY);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [parallaxEffect]);

  const slide = slides[currentSlide];

  return (
    <section className={`min-h-screen bg-black text-white flex items-center justify-center overflow-hidden relative ${className}`}>
      {/* Dynamic Background */}
      <div className="absolute inset-0 z-0 transition-all duration-1000 ease-in-out">
        {/* Video/Image Background */}
        <div
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ${parallaxEffect ? 'fixed' : ''}`}
          style={{
            backgroundImage: slide.bgImage ? `url('${slide.bgImage}')` : 'none',
            transform: parallaxEffect ? `translateY(${scrollY * 0.5}px)` : 'none',
            transition: 'background-image 1s ease-in-out, transform 0.1s ease-out',
            opacity: 0.3,
          }}
        />

        {/* Fallback Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-90"></div>

        {/* Animated Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-transparent to-black/80"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Particle Effect */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-30"
              initial={{
                x: Math.random() * 100 + '%',
                y: Math.random() * 100 + '%',
              }}
              animate={{
                y: [Math.random() * 100 + '%', Math.random() * 100 + '%', Math.random() * 100 + '%'],
                opacity: [0.1, 0.5, 0.1],
              }}
              transition={{
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col items-center"
          >
            {/* Logo Image */}
            {slide.useBrandLogo && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mb-8"
              >
                <Image 
                  src={isDark ? darkLogo : lightLogo} 
                  alt="Akiles Logo" 
                  width={360} 
                  height={360} 
                  className="w-96 md:w-[360px] h-auto drop-shadow-2xl"
                  priority
                />
              </motion.div>
            )}

            {/* Starburst Icon (Subtle placeholder for slides without logo) */}
            {!slide.useBrandLogo && !slide.hideStar && (
              <div className="mb-6 opacity-40">
                <motion.div
                  initial={{ rotate: -45, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="relative"
                >
                  <Star className="w-12 h-12 text-white" fill="currentColor" />
                </motion.div>
              </div>
            )}

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-2xl md:text-5xl font-black tracking-tight mb-4"
            >
              {slide.title}
            </motion.h1>

            {/* Subheadline */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto"
            >
              {slide.subtitle}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex gap-20 justify-center mt-6 flex-wrap"
            >
              <Link href={slide.btn1Href}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    boxShadow: [
                      '0 0 0 0px rgba(6, 182, 212, 0)',
                      '0 0 20px 2px rgba(6, 182, 212, 0.8)',
                      '0 0 0 0px rgba(6, 182, 212, 0)',
                      '0 0 40px 10px rgba(6, 182, 212, 0.2)',
                      '0 0 0 0px rgba(6, 182, 212, 0)',
                      '0 0 15px 5px rgba(6, 182, 212, 0.3)',
                      '0 0 0 0px rgba(6, 182, 212, 0)',
                    ],
                  }}
                  transition={{
                    boxShadow: { 
                      duration: 7, 
                      repeat: Infinity, 
                      ease: "easeInOut",
                      times: [0, 0.2, 0.4, 0.6, 0.8, 0.9, 1] 
                    },
                  }}
                  className="bg-gradient-to-r from-slate-100 via-cyan-600 to-slate-800 text-white font-bold px-8 py-4 rounded-lg hover:bg-gray-50 transition-all shadow-lg hover:border-cyan-400 hover:border-2 hover:shadow-2xl flex items-center"
                >
                <Play className="mr-2 h-5 w-5" />
                  {slide.btn1Text}
                  <Zap className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>

              <Link href={slide.btn2Href}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    boxShadow: [
                      '0 0 0 0px rgba(168, 85, 247, 0)',
                      '0 0 30px 5px rgba(168, 85, 247, 0.5)',
                      '0 0 0 0px rgba(168, 85, 247, 0)',
                      '0 0 15px 2px rgba(168, 85, 247, 0.3)',
                      '0 0 0 0px rgba(168, 85, 247, 0)',
                      '0 0 50px 15px rgba(168, 85, 247, 0.2)',
                      '0 0 0 0px rgba(168, 85, 247, 0)',
                    ],
                  }}
                  transition={{
                    boxShadow: { 
                      duration: 9, 
                      repeat: Infinity, 
                      ease: "easeInOut",
                      delay: 2.5,
                      times: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1]
                    },
                  }}
                  className="bg-gradient-to-r from-indigo-600 via-pink-500 to-cyan-500 text-white font-bold px-8 py-4 rounded-lg hover:from-indigo-500 hover:via-pink-600 hover:to-cyan-400 hover:border-white hover:border-2 transition-all shadow-lg hover:shadow-2xl flex items-center"
                >
                <Play className="mr-2 h-5 w-5" />
                  {slide.btn2Text}
                  <Star className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Slide Indicator Dots with Arrows */}
        {mounted && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center space-x-8">
            <button
              onClick={() => setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="flex space-x-4">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    i === currentSlide 
                      ? 'bg-purple-500 w-8' 
                      : 'bg-gray-600 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1))}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 w-full h-32 bg-gradient-to-t from-black via-transparent to-black opacity-30"></div>
    </section>
  );
}